import { Box, Divider, IconButton, Stack, Switch, Typography } from '@mui/material'
import { ChangeEvent, FC, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSetSettingsApiV1NotificationsFeedUserEventSettingsPatch } from '../../../api/gen/base'
import BackIcon from '../../../assets/icons/ArrowLeft.svg'
import CloseCircleIcon from '../../../assets/icons/CloseCircle.svg?react'
import { NOTIFICATIONS_ROUTE } from '../../../constants/routing.constants'
import { useUserStore } from '../../../store/storeUser'
import { useWrapperStore } from '../../../store/storeWrapper'
import { SidebarType } from '../../../types/sidebars'
import { RightSidebarWrapper } from '../RightSidebarWrapper'

type NotificationsSettings = {
    accountManagement: boolean
    news: boolean
    taskEvents: boolean
}

export const NotificationsSettingsSidebar: FC = () => {
    const { pathname } = useLocation()

    const [settings, setSettings] = useState<NotificationsSettings>({
        accountManagement: false,
        news: false,
        taskEvents: false,
    })

    const { setOpenSidebarType } = useWrapperStore((state) => state)
    const { userPersonalData, setUserPersonalData } = useUserStore((state) => state)

    const {
        mutate: mutateSettings,
        isPending,
        isSuccess,
        isError,
    } = useSetSettingsApiV1NotificationsFeedUserEventSettingsPatch()

    const handleBack = () => {
        setOpenSidebarType(SidebarType.NOTIFICATIONS)
    }

    const handleClose = () => {
        setOpenSidebarType(null)
    }

    useLayoutEffect(() => {
        if (userPersonalData) {
            const {
                feed_settings_account_management,
                feed_settings_news,
                feed_settings_task_events,
            } = userPersonalData

            setSettings({
                accountManagement: !!feed_settings_account_management,
                news: !!feed_settings_news,
                taskEvents: !!feed_settings_task_events,
            })
        }
    }, [userPersonalData])

    useEffect(() => {
        if (isSuccess) {
            setUserPersonalData({
                feed_settings_account_management: settings.accountManagement,
                feed_settings_news: settings.news,
                feed_settings_task_events: settings.taskEvents,
            })
        }
        if (isError && userPersonalData) {
            const {
                feed_settings_account_management,
                feed_settings_news,
                feed_settings_task_events,
            } = userPersonalData

            setSettings({
                accountManagement: !!feed_settings_account_management,
                news: !!feed_settings_news,
                taskEvents: !!feed_settings_task_events,
            })
        }
    }, [isSuccess, isError])

    const shouldDisplayBackButton = !pathname.includes(NOTIFICATIONS_ROUTE)

    const isAllChecked = useMemo(
        () =>
            (Object.keys(settings) as (keyof NotificationsSettings)[]).some(
                (settingKey) => settings[settingKey],
            ),
        [settings],
    )

    const handleUpdateSettings = (newSettings: NotificationsSettings) => {
        mutateSettings({
            data: {
                feed_settings_account_management: newSettings.accountManagement,
                feed_settings_news: newSettings.news,
                feed_settings_task_events: newSettings.taskEvents,
            },
        })
        setSettings(newSettings)
    }

    const handleChangeSetting = (fieldName: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const newSettings = {
            ...settings,
            [fieldName]: event.target.checked,
        }

        handleUpdateSettings(newSettings)
    }

    const handleToggleAllSettings = (event: ChangeEvent<HTMLInputElement>) => {
        const newSettings = (Object.keys(settings) as (keyof NotificationsSettings)[]).reduce(
            (acc, key) => {
                acc[key] = event.target.checked

                return acc
            },
            { ...settings },
        )

        handleUpdateSettings(newSettings)
    }

    return (
        <RightSidebarWrapper>
            <Stack
                sx={{
                    height: '100%',
                }}>
                <Stack
                    sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 16px 16px 24px',
                    }}>
                    <Stack
                        sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                        {shouldDisplayBackButton && (
                            <IconButton
                                size="large"
                                onClick={handleBack}
                                sx={{
                                    backgroundColor: '#F9FAFB',
                                }}>
                                <BackIcon />
                            </IconButton>
                        )}
                        <Stack direction="column">
                            <Typography variant="h5">Настройки уведомлений</Typography>
                        </Stack>
                    </Stack>

                    <IconButton size="large" sx={{ p: 0 }} onClick={handleClose}>
                        <CloseCircleIcon />
                    </IconButton>
                </Stack>
                <Divider
                    sx={{
                        borderColor: '#F4F4F6',
                    }}
                />
                <Stack
                    sx={{
                        height: '100%',
                        overflowY: 'auto',
                        p: 2,
                        gap: 2,
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            p: 2,
                            borderRadius: '16px',
                            backgroundColor: '#F9FAFB',
                            justifyContent: 'space-between',
                        }}>
                        <Stack>
                            <Typography variant="h6">Получение уведомлений</Typography>
                            <Typography variant="caption2" color="#9195A1">
                                Включение/отключение всех типов уведомлений
                            </Typography>
                        </Stack>
                        <Switch
                            checked={isAllChecked}
                            disabled={isPending}
                            onChange={handleToggleAllSettings}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: '16px',
                            border: '1px solid #E7E8EE',
                            gap: 2,
                        }}>
                        <Stack
                            sx={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}>
                            <Stack>
                                <Typography variant="h6">События задач</Typography>
                                <Typography variant="caption2" color="#9195A1">
                                    Ответы, расшифровки, генерации
                                </Typography>
                            </Stack>
                            <Switch
                                checked={settings.taskEvents}
                                disabled={isPending}
                                onChange={handleChangeSetting('taskEvents')}
                            />
                        </Stack>
                        <Divider
                            sx={{
                                borderColor: '#E7E8EE',
                            }}
                        />
                        <Stack
                            sx={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}>
                            <Stack>
                                <Typography variant="h6">Управление аккаунтом</Typography>
                                <Typography variant="caption2" color="#9195A1">
                                    События баланса, хранилищ, подписки
                                </Typography>
                            </Stack>
                            <Switch
                                checked={settings.accountManagement}
                                disabled={isPending}
                                onChange={handleChangeSetting('accountManagement')}
                            />
                        </Stack>
                        <Divider
                            sx={{
                                borderColor: '#E7E8EE',
                            }}
                        />
                        <Stack
                            sx={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}>
                            <Stack>
                                <Typography variant="h6">Новости сервиса</Typography>
                                <Typography variant="caption2" color="#9195A1">
                                    Новые модели, функции, значительные обновления сервиса,
                                    маркетинговые уведомления
                                </Typography>
                            </Stack>
                            <Switch
                                checked={settings.news}
                                disabled={isPending}
                                onChange={handleChangeSetting('news')}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Stack>
        </RightSidebarWrapper>
    )
}
